import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Rocket, X, Menu, Award, Bot, Calendar, DollarSign, 
  Phone, MessageCircle
} from 'lucide-react';

const QUICK_ACTIONS = [
  { 
    label: 'Get Started', 
    href: '/register', 
    icon: Rocket, 
    color: 'bg-[#A4D65E] text-[#1E4D2E]',
    primary: true
  },
  { 
    label: 'AI Assistant', 
    href: '/ai-assistant', 
    icon: Bot, 
    color: 'bg-blue-500 text-white' 
  },
  { 
    label: 'Find Funding', 
    href: '/grants', 
    icon: DollarSign, 
    color: 'bg-green-600 text-white' 
  },
  { 
    label: 'PROOF Badges', 
    href: '/proof', 
    icon: Award, 
    color: 'bg-purple-600 text-white' 
  },
  { 
    label: 'Contact Us', 
    href: '/contact', 
    icon: Phone, 
    color: 'bg-gray-700 text-white' 
  }
];

// Pages where the mobile CTA should NOT show
const EXCLUDED_PAGES = [
  '/login', '/register', '/forgot-password', '/checkout', '/donate',
  '/studio', '/portal', '/admin'
];

export function MobileCTA() {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();

  // Check if we should show on this page
  const shouldShow = !EXCLUDED_PAGES.some(page => location.pathname.startsWith(page));

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        setIsOpen(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  if (!shouldShow) return null;

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Bottom Bar */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
            data-testid="mobile-cta"
          >
            {/* Quick Actions Panel */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="bg-white border-t border-gray-200 shadow-2xl rounded-t-3xl p-4"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-gray-900">Quick Actions</h3>
                    <button 
                      onClick={() => setIsOpen(false)}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <X className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {QUICK_ACTIONS.slice(1).map((action) => (
                      <Link
                        key={action.href}
                        to={action.href}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className={`w-12 h-12 rounded-full ${action.color} flex items-center justify-center`}>
                          <action.icon className="h-5 w-5" />
                        </div>
                        <span className="text-xs font-medium text-gray-700 text-center">{action.label}</span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Main CTA Bar */}
            <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
              {/* Menu Toggle */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`p-3 rounded-full transition-colors ${isOpen ? 'bg-gray-100' : 'bg-gray-50 hover:bg-gray-100'}`}
              >
                {isOpen ? (
                  <X className="h-5 w-5 text-gray-600" />
                ) : (
                  <Menu className="h-5 w-5 text-gray-600" />
                )}
              </button>

              {/* Primary CTA */}
              <Link to="/register" className="flex-1">
                <Button className="w-full bg-[#006847] hover:bg-[#005538] text-white font-semibold py-6">
                  <Rocket className="h-5 w-5 mr-2" />
                  Get Started Free
                </Button>
              </Link>

              {/* AI Quick Access */}
              <Link to="/ai-assistant">
                <button className="p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors">
                  <Bot className="h-5 w-5" />
                </button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default MobileCTA;
